import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Store, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['health', 'pension', 'insurance', 'food', 'transport', 'housing', 'lifestyle', 'education', 'other'];

export default function GlobalVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'health', contact_email: '', contact_phone: '', bank_name: '', bank_code: '', account_number: '', account_name: '', is_global: true });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => { try { setVendors(await superAdminAPI.getVendors()); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await superAdminAPI.createVendor(form); toast.success('Vendor created'); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await superAdminAPI.deleteVendor(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-6" data-testid="global-vendors">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-gray-900">Global Vendors</h1><p className="text-sm text-gray-500">{vendors.length} vendors</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-pink-500 hover:bg-pink-600 text-white"><Plus className="w-4 h-4 mr-2" /> Add Vendor</Button></DialogTrigger>
          <DialogContent className="border-gray-200 max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Global Vendor</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div><Label className="text-sm">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="h-9" /></div>
              <div><Label className="text-sm">Category</Label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-9 rounded-md border border-gray-200 text-sm px-3">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} required className="h-9" /></div>
                <div><Label className="text-sm">Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Bank Name</Label><Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} required className="h-9" /></div>
                <div><Label className="text-sm">Bank Code</Label><Input value={form.bank_code} onChange={e => setForm({...form, bank_code: e.target.value})} required className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Account No</Label><Input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} required className="h-9" /></div>
                <div><Label className="text-sm">Account Name</Label><Input value={form.account_name} onChange={e => setForm({...form, account_name: e.target.value})} required className="h-9" /></div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Vendor'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {vendors.map(v => (
          <Card key={v.id} className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><Store className="w-4 h-4" /></div>
                  <div><p className="text-sm font-medium text-gray-900">{v.name}</p><p className="text-xs text-gray-400">{v.contact_email}</p></div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(v.id)} className="text-gray-400 hover:text-red-500 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-50 text-pink-600 font-medium">{v.category}</span>
                {v.is_global && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">Global</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
